import { useEffect, useState } from 'react'
import { useQuery } from '@apollo/client/react'
import { PROMOTIONAL_IMAGES } from '../api/queries'

interface PromotionalImage {
  id: string
  url: string
  /** Versión de la imagen para el carrusel móvil; sin ella el slide se omite en móvil. */
  urlMobile: string | null
  link: string | null
  order: number
  title: string | null
}

interface PromotionalImagesData {
  promotionalImages: PromotionalImage[]
}

const DEFAULT_CAPTION = (
  <>
    Todo tu negocio.<br />
    <span className="text-primary">En una sola plataforma.</span>
  </>
)

/**
 * Panel lateral promocional (lobby) del flujo de auth.
 *
 * Carga las imágenes promocionales administradas desde Identidad
 * (identity-web → /configurations/lobby) vía la query pública del gateway,
 * sin token. Si no hay imágenes activas o la query falla (gateway caído,
 * sin conexión), se queda con el diseño decorativo local: el formulario
 * de login nunca depende de esta carga.
 */
/** Rotación automática cada 5 s si hay más de un slide. */
function useCarouselIndex(length: number) {
  const [idx, setIdx] = useState(0)
  useEffect(() => {
    if (length < 2) return
    const t = setInterval(() => setIdx((i) => (i + 1) % length), 5000)
    return () => clearInterval(t)
  }, [length])
  return { idx, setIdx }
}

export function LobbyPromo() {
  const { data } = useQuery<PromotionalImagesData>(PROMOTIONAL_IMAGES, {
    context: { skipAuth: true },
    fetchPolicy: 'cache-first',
  })
  // Ya vienen solo las activas y ordenadas por `order` asc desde el gateway.
  const slides = data?.promotionalImages ?? []
  const { idx, setIdx } = useCarouselIndex(slides.length)
  const [loaded, setLoaded] = useState(false)

  const current = slides.length > 0 ? (slides[idx % slides.length] ?? null) : null

  const img = current && (
    <img
      key={current.url}
      src={current.url}
      alt=""
      loading="lazy"
      onLoad={() => setLoaded(true)}
      className={`h-full w-full object-cover object-center transition-opacity duration-500 ${
        loaded ? 'opacity-100' : 'opacity-0'
      }`}
    />
  )

  return (
    <aside className="hidden lg:block relative bg-foreground text-background overflow-hidden">
      {/* Fondo decorativo local: visible mientras carga, sin imágenes activas o con la query fallida */}
      <div className="absolute inset-0 bg-[radial-gradient(60%_50%_at_70%_30%,rgba(217,96,46,0.35),transparent_70%)]" />
      <div className="absolute inset-0 opacity-[0.08]" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />

      {current && (
        <div className="absolute inset-0">
          {current.link ? (
            <a href={current.link} target="_blank" rel="noreferrer" className="block h-full w-full">
              {img}
            </a>
          ) : (
            img
          )}
          {/* Velo inferior para que el caption sea legible sobre cualquier imagen */}
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
        </div>
      )}

      <div className="pointer-events-none relative h-full flex flex-col justify-end p-12">
        <h2 key={current?.title ?? 'claim'} className="text-4xl font-bold leading-tight animate-fade-up">
          {current?.title || DEFAULT_CAPTION}
        </h2>
        {!current && (
          <p className="text-neutral-400 mt-4 max-w-md">Más de 2,500 negocios usan TecoPOS para vender, controlar inventario y fidelizar clientes.</p>
        )}
        {/* Indicadores del carrusel */}
        {slides.length > 1 && (
          <div className="pointer-events-auto mt-5 flex gap-1.5">
            {slides.map((s, i) => (
              <button
                key={s.id}
                onClick={() => setIdx(i)}
                aria-label={`Imagen ${i + 1}`}
                className={`h-1.5 rounded-full transition-all ${
                  i === idx % slides.length ? 'w-6 bg-primary' : 'w-1.5 bg-white/40 hover:bg-white/70'
                }`}
              />
            ))}
          </div>
        )}
      </div>
    </aside>
  )
}

/**
 * Carrusel móvil (< lg) del lobby, para debajo del formulario de login:
 * horizontal compacto, solo con los slides que tengan `urlMobile`. Sin
 * ninguno (o con la query fallida), no renderiza nada. Comparte la misma
 * query que LobbyPromo: Apollo dedup/cache evita una segunda petición.
 */
export function LobbyPromoMobile() {
  const { data } = useQuery<PromotionalImagesData>(PROMOTIONAL_IMAGES, {
    context: { skipAuth: true },
    fetchPolicy: 'cache-first',
  })
  const slides = (data?.promotionalImages ?? []).filter((s) => !!s.urlMobile)
  const { idx, setIdx } = useCarouselIndex(slides.length)

  if (slides.length === 0) return null

  const current = slides[idx % slides.length] ?? slides[0]

  const img = (
    <img
      key={current.urlMobile as string}
      src={current.urlMobile as string}
      alt=""
      loading="lazy"
      className="h-full w-full object-cover object-center"
    />
  )

  return (
    <div className="mt-8 w-full lg:hidden">
      <div className="relative aspect-[21/9] overflow-hidden rounded-3xl border border-black/5 shadow-sm">
        {current.link ? (
          <a href={current.link} target="_blank" rel="noreferrer" className="block h-full w-full">
            {img}
          </a>
        ) : (
          img
        )}
        {/* Velo inferior para que el caption sea legible sobre cualquier imagen */}
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
        <div className="pointer-events-none absolute inset-x-4 bottom-3 text-white">
          <p key={current.title ?? 'claim'} className="truncate text-sm font-semibold animate-fade-up">
            {current.title || 'Todo tu negocio. En una sola plataforma.'}
          </p>
        </div>
      </div>
      {/* Indicadores del carrusel */}
      {slides.length > 1 && (
        <div className="mt-3 flex justify-center gap-1.5">
          {slides.map((s, i) => (
            <button
              key={s.id}
              onClick={() => setIdx(i)}
              aria-label={`Imagen ${i + 1}`}
              className={`h-1.5 rounded-full transition-all ${
                i === idx % slides.length ? 'w-6 bg-primary' : 'w-1.5 bg-foreground/20 hover:bg-foreground/40'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  )
}
